// "מסירה ללקוח משווק" — the close screen's pure logic (dealer P2-B).
//
// Backend: POST /api/dealer/opportunity/{id}/close-business-delivery (services/business_delivery.py).
// The rules here MIRROR the server's validate_plan so the screen says it first; the server is
// the one that enforces them.
//   * one activity per ADDRESS (owner) — the seller splits each line's units across addresses
//   * every unit of every deliverable line goes to exactly one address
//   * a line already at the customer (Device_At_Customer__c) is not delivered
//   * "no invoice" needs a reason

// Seeded by migration 2026-10-11_business_delivery_worktype.sql. Matched by Id, never by name:
// a SERVICE work type called "מסירה" also exists.
export const BUSINESS_DELIVERY_WORK_TYPE_ID = 'awoBIZDELIVERY1';
export const BUSINESS_DELIVERY_WORK_TYPE_NAME = 'מסירה ללקוח משווק';

export function isBusinessDeliveryWorkType(wt) {
  return !!wt && wt.Id === BUSINESS_DELIVERY_WORK_TYPE_ID;
}

// "תיאום מסירה משווקים" (dealer P4, ADR-R2-09). The button id is the one the profile allow list
// is keyed on: ('ServiceAppointment', 'dealerDeliverySchedule'), edited in Setup → buttons.
export const DELIVERY_SCHEDULE_BUTTON_ID = 'dealerDeliverySchedule';
export const DELIVERY_SCHEDULE_LABEL = 'תיאום מסירה משווקים';

/** Show the button on this record? The server decides who may (bootstrap.canScheduleDeliveries);
 *  this only decides WHERE: a delivery activity (by work-type Id), or a closed delivery opportunity. */
export function showsDeliverySchedule(boot, objectName, record) {
  if (!boot || !boot.canScheduleDeliveries || !record) return false;
  if (objectName === 'ServiceAppointment') return record.WorkTypeId === BUSINESS_DELIVERY_WORK_TYPE_ID;
  if (objectName === 'Opportunity') return record.Type === BUSINESS_DELIVERY_WORK_TYPE_NAME;
  return false;
}

const qty = (v) => {
  const n = parseInt(v, 10);
  return Number.isNaN(n) || n < 1 ? 1 : n;
};

const atCustomer = (li) => ['1', 'true'].includes(String(li.Device_At_Customer__c || '').toLowerCase());

/** [{id, name, quantity}] — the lines that are actually delivered. */
export function deliverableLines(lineItems, productNames = {}, pbeToProduct = {}) {
  return (lineItems || []).filter(li => !atCustomer(li)).map(li => {
    const pid = li.Product2Id || pbeToProduct[li.PricebookEntryId];
    const name = li.Product2Id__Name || li.Product2__Name || li.ProductName || productNames[pid] || li.Name || 'מוצר';
    return { id: li.Id, name, quantity: qty(li.Quantity) };
  });
}

export function totalUnits(lines) {
  return (lines || []).reduce((s, l) => s + l.quantity, 0);
}

/** {lineId: [units per address]} — everything at the first address. */
export function defaultAllocation(lines, nAddresses) {
  const out = {};
  (lines || []).forEach(l => {
    out[l.id] = Array.from({ length: nAddresses }, (_, i) => (i === 0 ? l.quantity : 0));
  });
  return out;
}

/** Hebrew messages; empty = the allocation can be submitted. */
export function allocationErrors(lines, allocation, nAddresses) {
  const errors = [];
  (lines || []).forEach(l => {
    const row = (allocation && allocation[l.id]) || [];
    const sum = row.reduce((s, v) => s + (Number(v) || 0), 0);
    if (row.some(v => !Number.isInteger(Number(v)) || Number(v) < 0)) {
      errors.push(`${l.name}: כמות לא תקינה`);
    } else if (sum !== l.quantity) {
      errors.push(`${l.name}: שובצו ${sum} מתוך ${l.quantity} יחידות`);
    }
  });
  for (let i = 0; i < nAddresses; i += 1) {
    const used = (lines || []).some(l => Number(((allocation || {})[l.id] || [])[i]) > 0);
    if (!used) errors.push(`כתובת ${i + 1}: אין מה למסור`);
  }
  return errors;
}

/** Hebrew messages; empty = the addresses can be submitted. */
export function addressErrors(addresses, mode) {
  const errors = [];
  (addresses || []).forEach((a, i) => {
    const label = mode === 'multi' ? `כתובת ${i + 1}` : 'כתובת המסירה';
    if (!String(a.street || '').trim() || !String(a.city || '').trim()) errors.push(`${label}: חסרים רחוב ועיר`);
    if (mode === 'multi' && (!String(a.contact_name || '').trim() || !String(a.contact_phone || '').trim())) {
      errors.push(`${label}: חסרים שם וטלפון של איש קשר`);
    }
  });
  return errors;
}

/** The exact body of POST /close-business-delivery. */
export function buildCloseBody({ stage, issueInvoice, invoiceSkipReason, issueDeliveryNotes, mode, addresses,
                                 allocation, lines }) {
  const n = mode === 'multi' ? (addresses || []).length : 1;
  const groups = Array.from({ length: n }, (_, i) => ({
    address_idx: i,
    line_items: (lines || [])
      .map(l => ({ id: l.id, quantity: Number(((allocation || {})[l.id] || [])[i]) || 0 }))
      .filter(x => x.quantity > 0),
  }));
  return {
    stage_name: stage || 'סגירה בהצלחה',
    issue_invoice: !!issueInvoice,
    invoice_skip_reason: issueInvoice ? '' : String(invoiceSkipReason || '').trim(),
    issue_delivery_notes: !!issueDeliveryNotes,
    mode,
    addresses: mode === 'single_account' ? [] : (addresses || []).map(a => ({
      street: a.street || '', city: a.city || '', postal_code: a.postalCode || a.postal_code || '',
      latitude: String(a.latitude || ''), longitude: String(a.longitude || ''),
      floor: a.floor || '', entrance: a.entrance || '', apartment: a.apartment || '',
      contact_name: a.contact_name || '', contact_phone: a.contact_phone || '',
    })),
    groups,
  };
}

/** How a document row reads to the seller. */
export function documentLabel(d) {
  const kind = d.kind === 'tax_invoice' ? 'חשבונית מס' : 'תעודת משלוח';
  const where = d.kind === 'delivery_note' && (d.street || d.city)
    ? ` — ${[d.street, d.city].filter(Boolean).join(', ')}` : '';
  const state = {
    pending: 'בהפקה…', issued: `הונפקה${d.rivhit_number ? ` מס' ${d.rivhit_number}` : ''}`,
    failed: `נכשלה${d.error ? `: ${d.error}` : ''}`, skipped: `לא הופקה${d.skip_reason ? ` (${d.skip_reason})` : ''}`,
  }[d.state] || d.state;
  return `${kind}${where}: ${state}`;
}

export const hasPendingDocuments = (docs) => (docs || []).some(d => d.state === 'pending');
